import type { Reducer } from "redux";

import type { Action, AnyAction } from "./actionCreators";

export class ReducerChain<State, UsedActions extends string = never> {
  private initialState: State;
  private reducers = new Map();

  public constructor(initialState: State) {
    this.initialState = initialState;
  }

  /**
   * Update state upon any action of type `ActionType`.
   *
   * Usage:
   *
   * ```typescript
   * reducerChain.on(MyActionEnumType.start, (state, action: ReturnType<typeof myAction.start>) => {
   *   // Update state.
   * });
   * ```
   */
  public on<ActionType extends string, TargetAction extends Action<ActionType>>(
    key: ActionType,
    handler: (state: State, action: TargetAction) => unknown
  ): ReducerChain<State, UsedActions | ActionType>;

  /**
   * Update state upon an action created by the specific Action Creator
   *
   * Usage:
   *
   * ```typescript
   *
   * const myAction = createAction(..., (payload: string) => ({ payload }));
   *
   * reducerChain.on(myAction, (state, ({ payload })) => {
   *   // Update state with payload
   * });
   * ```
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public on<TKey extends string, TActionCreator extends (...args: any[]) => Action<TKey>>(
    key: TActionCreator & { getType: () => TKey },
    handler: (state: State, action: ReturnType<TActionCreator>) => State
  ): ReducerChain<State, UsedActions | ReturnType<TActionCreator>["type"]>;

  public on(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    key: string | { (...args: any[]): AnyAction; getType: () => string },
    handler: (state: State, action: AnyAction) => unknown
  ) {
    if (typeof key === "string") {
      this.reducers.set(key, handler);
    } else {
      this.reducers.set(key.getType(), handler);
    }

    return this;
  }

  public build<AllActionKeys extends UsedActions>(): Reducer<State, Action<AllActionKeys>> {
    return (state: State = this.initialState, action: Action<string>): State => {
      if (this.reducers.has(action.type)) {
        return this.reducers.get(action.type)(state, action);
      }
      return state;
    };
  }
}
